import { isEmpty } from 'radash';
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { Accordion } from '@/components/core/Accordion';
import { AccordionSingle } from '@/components/core/Accordion/Accordion';
import { Input } from '@/components/core/Input/Input';
import { useAttributesStateStore } from '@/components/datamodel/hooks/useAttributesStore';
import { useSections } from '@/components/datamodel/hooks/useSections';
import { AUTH_OBJECT_TYPES, useAuth } from '@/hooks/useAuth';
import { useCRUDView } from '@/hooks/useCRUDView';
import { useDataModel } from '@/hooks/useDataModel';
import { useTranslation } from '@/hooks/useTranslation';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { DataModelAttribute, PersistedItem } from '@/types/DataModel';

import { FocusAttributeContext } from '../../Context/FocusAttributeContext';
import { ValidationProvider } from '../../Context/ValidationContext';
import { resolveEditorDeepLink } from '../../editorDeepLink';
import { useDefaultItem } from '../../hooks/useDefaultItem';
import { useValidations } from '../../hooks/validation';
import { ItemOrigin } from '../../ItemEditor.types';
import { scrollOpenSectionAfterOpenAnimation } from '../../scrollOpenSectionIntoView';
import { CategorySection } from './components/CategorySection';
import { Section } from './components/Section';

interface ItemEditorTabProps {
    initialCategory?: string;
    editorState: any;
    forceCloseTask: any;
    originalPrimaryKey: string | null;
    persistedItem?: PersistedItem;
    itemOrigin?: ItemOrigin;
    templateForPreparedDefaultItem?: object;
    task: string;
    openSections: string[];
    setOpenSections: React.Dispatch<React.SetStateAction<string[]>>;
    handleReloadItemDialogCloseFromDialog?: () => void;
    scrollStickySection?: boolean;
    onStickySectionScrolled?: () => void;
}

const ItemEditorTab = ({
    initialCategory,
    persistedItem,
    itemOrigin,
    templateForPreparedDefaultItem,
    originalPrimaryKey,
    openSections,
    setOpenSections,
    scrollStickySection = false,
    onStickySectionScrolled,
}: ItemEditorTabProps) => {
    const $angularJSInjector = getInjector();
    const $rootScope = getRootScope($angularJSInjector);
    const $location = $angularJSInjector.get('$location');
    const $timeout = $angularJSInjector.get('$timeout');

    const { focusAttribute, setFocusAttribute, pinnedAttributeName, setPinnedAttributeName }: any = useContext(FocusAttributeContext);
    const { dataModel } = useDataModel();
    const { setValue, reset, getValues } = useFormContext();
    const hydratedItemKeyRef = useRef<string | null>(null);
    const { onEnterViewName, setView, closeOtherPanels } = useCRUDView();
    const { t } = useTranslation();
    const isPreparedDefaultOrigin = itemOrigin === ItemOrigin.PREPARED_DEFAULT;
    // Do not treat an in-flight prepared-default as a plain new item: that
    // would GET /items/defaultItem and overwrite the predialog template.
    const creatingNewItem = isEmpty(persistedItem) && !isPreparedDefaultOrigin;
    // Predialog create still has a persistedItem (the prepared default), so
    // creatingNewItem is false — but we must flush a full validate like a
    // new item. Do not skip the first queue tick. See LAX-10254.
    const runInitialFullValidation =
        creatingNewItem || isPreparedDefaultOrigin;
    const [isPersistedItemLoaded, setIsPersistedItemLoaded] = useState<boolean>(false);
    const [isDefaultItemLoaded, setIsDefaultItemLoaded] = useState(false);

    const selectedCategory = useWatch({
        name: 'category__'
    });
    const { auth } = useAuth();
    const layoutSectionsLoaded = useRef(false);
    const action = creatingNewItem ? 'create' : 'edit';
    const allDataModelCategories = dataModel?.allCategories();

    // FIXME: Remove this, already "Category" component does this.
    // Memoize categories filtering to prevent unnecessary recalculations
    const availableCategories = useMemo(() => {
        if (!allDataModelCategories || !auth) return [];

        allDataModelCategories.filter((category) => {
            return (
                selectedCategory === category.name ||
                auth.hasAnyPermission(AUTH_OBJECT_TYPES.OBJECT_TYPE_ITEMS, action, {
                    category__: category.name,
                })
            );
        });
    }, [allDataModelCategories, auth, action, selectedCategory]);

    const { defaultItem } = useDefaultItem(creatingNewItem, selectedCategory);
    const { layoutSections } = useSections(selectedCategory);

    useEffect(() => {
        if (!initialCategory) return;

        setValue('category__', initialCategory, { shouldDirty: false });
    }, [initialCategory, setValue]);

    const setAttributeUrl = useCallback((attribute? : DataModelAttribute, options?: { focus?: boolean }) => {
        let url: string;

        if (selectedCategory) {
            if (originalPrimaryKey) {
                url = `/copy/${originalPrimaryKey}`;
            } else if (persistedItem?.primaryKey__) {
                url = `/edit/${persistedItem.primaryKey__}`;
            } else {
                url = '/edit/new';
            }

            if (attribute) url += `/attribute/${attribute.name}`;
            // Focusing an input scrolls it into view and pulls a sticky section
            // back to the top of the editor. Skip that when the open section
            // is being restored across items.
            if (options?.focus !== false) {
                setFocusAttribute(attribute);
            }
            $timeout(() => {
                $location.displayUrl(url);
            });
        }
    }, [selectedCategory, originalPrimaryKey, persistedItem?.primaryKey__, setFocusAttribute, $timeout, $location]);

    const findSectionForAttribute = useCallback((attributeName: string, sections: any[]) => {
        if (!sections) return undefined;

        const filteredSections = sections.filter(section =>
            section.attributes.some((attr: any) => attr.name === attributeName)
        );

        if (filteredSections.length === 0) return undefined;
        if (filteredSections.length === 1) return filteredSections[0];

        return (
            filteredSections.find(section => section.name === attributeName.toUpperCase()) ||
            filteredSections[0]
        );
    }, []);

    useValidations({
        isReadyForInitialSetup: creatingNewItem ? !!selectedCategory && isDefaultItemLoaded : isPersistedItemLoaded,
        isNewItem: runInitialFullValidation,
        initialItem: creatingNewItem ? defaultItem : persistedItem,
        initialValidations: isPreparedDefaultOrigin
            ? undefined
            : persistedItem?.__validations__,
    });

    const itemHydrationKey = creatingNewItem
        ? `new:${selectedCategory ?? ''}`
        : (persistedItem?.primaryKey__ ?? null);

    // Paint the editor immediately with the predialog template (no Loader).
    // When the prepared-default POST returns, replace with the full item
    // and apply hidden/readonly before the next paint. See LAX-10254.
    useLayoutEffect(() => {
        if (!isPreparedDefaultOrigin) {
            return;
        }
        if (persistedItem) {
            if (persistedItem.attributeStates__) {
                useAttributesStateStore.getState().initializeWithStates(persistedItem.attributeStates__);
            }
            reset(persistedItem, { keepDirty: true });
            setIsPersistedItemLoaded(true);
            return;
        }
        reset({
            ...(templateForPreparedDefaultItem || {}),
            category__: initialCategory,
        }, { keepDirty: true });
    }, [isPreparedDefaultOrigin, persistedItem, templateForPreparedDefaultItem, initialCategory, reset]);

    useEffect(() => {
        if (defaultItem) {
            const isFirstHydration = hydratedItemKeyRef.current !== itemHydrationKey;
            reset(defaultItem, { keepDirty: !isFirstHydration });
            setIsDefaultItemLoaded(true);
        }
        //eslint-disable-next-line
    }, [defaultItem]);

    useEffect(() => {
        if (persistedItem && itemOrigin !== ItemOrigin.PREPARED_DEFAULT) {
            const isFirstHydration = hydratedItemKeyRef.current !== itemHydrationKey;
            reset(persistedItem, { keepDirty: !isFirstHydration });
            setIsPersistedItemLoaded(true);
        }
        //eslint-disable-next-line
    }, [persistedItem, itemOrigin]);

    // Controllers may register missing leaves as '' after reset. Adopt those
    // mounted values as the pristine snapshot so opening a saved item is not
    // dirty (History / hierarchy tabs stay reachable).
    // Layout sections load after the item, and Next/Previous remounts this tab
    // against a cleared form. Snapshot only once those controllers are mounted,
    // then once more so an Angular template link that writes in the next turn
    // is included. See LAX-10350.
    useEffect(() => {
        if (isPreparedDefaultOrigin) return;
        if (!itemHydrationKey) return;
        if (creatingNewItem && !isDefaultItemLoaded) return;
        if (!creatingNewItem && !isPersistedItemLoaded) return;
        if (!creatingNewItem && !layoutSections?.length) return;
        if (hydratedItemKeyRef.current === itemHydrationKey) return;

        let followUpId = 0;
        const timeoutId = window.setTimeout(() => {
            reset(getValues(), { keepDirty: false });
            followUpId = window.setTimeout(() => {
                reset(getValues(), { keepDirty: false });
                hydratedItemKeyRef.current = itemHydrationKey;
            }, 0);
        }, 0);

        return () => {
            window.clearTimeout(timeoutId);
            window.clearTimeout(followUpId);
        };
    }, [
        itemHydrationKey,
        creatingNewItem,
        isDefaultItemLoaded,
        isPersistedItemLoaded,
        isPreparedDefaultOrigin,
        layoutSections,
        getValues,
        reset,
    ]);

    // focus attribute can be changed from editor header -> search attribute
    useEffect(() => {
        if (focusAttribute && focusAttribute.section) {
            setOpenSections(prev => {
                if (closeOtherPanels) {
                    // For AccordionSingle, set the target section as the first (and only) open section
                    return [focusAttribute.section];
                } else {
                    // For Accordion (multiple), add the section to the array
                    return [...new Set([...prev, focusAttribute.section])];
                }
            });
        }
    }, [focusAttribute, closeOtherPanels]);

    useEffect(() => {
        if (layoutSections && layoutSections.length > 0 && !layoutSectionsLoaded.current) {
            // Only set the first section to open once, when layoutSections are loaded
            const currentPath = $location.path();
            const deepLink = resolveEditorDeepLink(currentPath);
            const sectionFromUrl = deepLink.sectionName
                ? layoutSections.find((section) =>
                    String(section.name).toLowerCase() === deepLink.sectionName?.toLowerCase())
                : undefined;
            const sectionAndAttribute = !sectionFromUrl && deepLink.attributeName
                ? findSectionForAttribute(deepLink.attributeName, layoutSections)
                : undefined;

            const allSectionsNames = new Set(layoutSections.map((section) => section.name));
            let attributeToFocus: DataModelAttribute | undefined;
            let keepStickySection = false;

            setOpenSections((prev) => {
                const intersection = new Set(prev.filter((section) => allSectionsNames.has(section)));

                const currentOpenSection = layoutSections.find((section) =>
                    intersection.has(section.name)
                );
                const pinnedSection = pinnedAttributeName
                    ? findSectionForAttribute(pinnedAttributeName, layoutSections)
                    : undefined;
                const pinnedAttribute = pinnedSection?.attributes
                    ?.find((attr: DataModelAttribute) => attr.name === pinnedAttributeName);

                if (pinnedAttribute && pinnedSection) {
                    // The user focused this field. Keep it on the next item
                    // instead of scrolling the section back to the top.
                    attributeToFocus = pinnedAttribute;
                    if (closeOtherPanels) {
                        intersection.clear();
                    }
                    intersection.add(pinnedSection.name);
                } else if (currentOpenSection) {
                    if (pinnedAttributeName) {
                        setPinnedAttributeName(undefined);
                    }
                    // A section carried over from the previous item wins over the
                    // attribute still sitting in the URL, which is usually GTIN.
                    keepStickySection = true;
                    attributeToFocus = currentOpenSection.attributes[0];
                } else if (sectionFromUrl) {
                    attributeToFocus = sectionFromUrl.attributes?.[0];
                    intersection.add(sectionFromUrl.name);
                } else if (sectionAndAttribute) {
                    // - First try to focus the attribute provided in the URL
                    attributeToFocus = sectionAndAttribute?.attributes
                        ?.filter((attr: any) => attr.name === deepLink.attributeName)[0];
                    intersection.add(sectionAndAttribute?.name);

                } else if (layoutSections[0].attributes.length > 0) {
                    // - otherwise focus the first attribute of the first section
                    attributeToFocus = layoutSections[0].attributes[0];
                    intersection.add(layoutSections[0].name);
                }

                setAttributeUrl(attributeToFocus, { focus: !keepStickySection });
                layoutSectionsLoaded.current = true;
                return [...intersection];
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layoutSections, creatingNewItem]);

    useEffect(() => {
        if (!scrollStickySection || !onStickySectionScrolled || !layoutSections?.length) {
            return;
        }

        const pinnedSection = pinnedAttributeName
            ? findSectionForAttribute(pinnedAttributeName, layoutSections)
            : undefined;
        if (pinnedSection) {
            onStickySectionScrolled();
            return;
        }

        const openSection = layoutSections.find((section) => openSections.includes(section.name));
        if (!openSection) {
            onStickySectionScrolled();
            return;
        }

        const sectionEl = document.querySelector(
            `#editor-modal-body [data-section-name="${CSS.escape(openSection.name)}"]`,
        );
        if (!(sectionEl instanceof HTMLElement)) {
            onStickySectionScrolled();
            return;
        }

        return scrollOpenSectionAfterOpenAnimation(sectionEl, onStickySectionScrolled);
    }, [scrollStickySection, layoutSections, openSections, onStickySectionScrolled, pinnedAttributeName, findSectionForAttribute]);

    // Debounced accordion change handler to prevent rapid state updates
    const debouncedAccordionChange = useRef<NodeJS.Timeout | null>(null);
    const handleAccordionChange = useCallback((sections: string[]) => {
        // Clear any existing timeout
        if (debouncedAccordionChange.current) {
            clearTimeout(debouncedAccordionChange.current);
        }

        // Set a new timeout to update state after a short delay
        debouncedAccordionChange.current = setTimeout(() => {
            setOpenSections(sections);
            const pinnedSectionName = pinnedAttributeName
                ? findSectionForAttribute(pinnedAttributeName, layoutSections ?? [])?.name
                : undefined;
            if (pinnedSectionName && !sections.includes(pinnedSectionName)) {
                setPinnedAttributeName(undefined);
            }
        }, 50); // 50ms debounce delay
    }, [pinnedAttributeName, layoutSections, findSectionForAttribute, setPinnedAttributeName, setOpenSections]);

    // callback removed; logic centralized in ItemEditorDialog

    const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        $rootScope.isDirty = true;
        setView(prev => { prev.name = event.target.value; return prev; });
    }, [$rootScope, setView]);

    // Memoize the section components to prevent unnecessary re-renders
    const sectionComponents = useMemo(() =>
        layoutSections?.map(section =>
            <Section
                key={section.name}
                section={section}
                isOpen={openSections.includes(section.name)}
                primaryKey={persistedItem?.primaryKey__}
                scrollIntoViewOnOpen={closeOtherPanels}
            />
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layoutSections, openSections, persistedItem?.primaryKey__,closeOtherPanels]
    );

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (debouncedAccordionChange.current) {
                clearTimeout(debouncedAccordionChange.current);
            }
        };
    }, []);

    if (!dataModel) {
        return null;
    }

    // If we are not creating a new item and the layout is not loaded yet, skip
    // this render cycle so we can open up the first section initially.
    if (!creatingNewItem && !layoutSections) {
        return null;
    }

    if (onEnterViewName) {
        return <>
            <label className='tw-max-w-[250px] tw-text-t-sm'>{t('VIEW.SET_NAME')}</label>
            <Input onChange={(event)=> handleInputChange(event)}></Input>
        </>;
    }

    return (
        <ValidationProvider>
            <div id='editor-modal-body'>
                {closeOtherPanels && <AccordionSingle
                    id="accordion"
                    data-last-selected={selectedCategory}
                    className="tw-w-full"
                    type="single"
                    value={layoutSections ? openSections[0] : 'category'}
                    onValueChange={(value)=> handleAccordionChange ([value])}
                >

                    <CategorySection initialSelectedCategory={initialCategory} isNewItem={creatingNewItem} categories={availableCategories} />

                    {sectionComponents}
                </AccordionSingle>}

                {!closeOtherPanels && <Accordion
                    id="accordion"
                    data-last-selected={selectedCategory}
                    className="tw-w-full"
                    type="multiple"
                    // Initially open up the category section if we are creating a new item, otherwise open up the first section in the layout
                    value={openSections}
                    onValueChange={handleAccordionChange}
                >
                    <CategorySection initialSelectedCategory={initialCategory} isNewItem={creatingNewItem} categories={availableCategories} />

                    {sectionComponents}
                </Accordion>}
            </div>
        </ValidationProvider>
    );
};

export { ItemEditorTab };
